// Script to populate database with 50 random profiles and wallets
import { db } from './firebase';
import { doc, setDoc, serverTimestamp, getDoc, deleteField, deleteDoc, writeBatch, runTransaction } from 'firebase/firestore';
import { getNextProfileId } from './userMappingService';
import { 
  randomNames, 
  randomUsernames, 
  allTags, 
  allGroups, 
  socialPlatforms, 
  personalDataFields,
  birthYears,
  worldLocations,
  getAvatarUrl
} from './profilesData';
import { encryptProfile, uploadToPinata } from './cryptoOperations';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha2';
import { BADGES } from './services/gamificationService';

// Popular songs list for random selection (unique songs only)
const popularSongs = [
  'Blinding Lights - The Weeknd',
  'Shape of You - Ed Sheeran',
  'Someone You Loved - Lewis Capaldi',
  'Bad Guy - Billie Eilish',
  'Watermelon Sugar - Harry Styles',
  'Levitating - Dua Lipa',
  'Good 4 U - Olivia Rodrigo',
  'Stay - The Kid LAROI & Justin Bieber',
  'Heat Waves - Glass Animals',
  'As It Was - Harry Styles',
  'Flowers - Miley Cyrus',
  'Anti-Hero - Taylor Swift',
  'Unholy - Sam Smith & Kim Petras',
  'About Damn Time - Lizzo',
  'Running Up That Hill - Kate Bush',
  'First Class - Jack Harlow',
  'Late Night Talking - Harry Styles',
  'Break My Soul - Beyoncé',
  'Industry Baby - Lil Nas X & Jack Harlow',
  'Easy On Me - Adele',
  'Shivers - Ed Sheeran',
  'Cold Heart - Elton John & Dua Lipa',
  'Peaches - Justin Bieber ft. Daniel Caesar & Giveon',
  'Montero - Lil Nas X',
  'Save Your Tears - The Weeknd & Ariana Grande',
  'Dynamite - BTS',
  'Butter - BTS',
  'Kiss Me More - Doja Cat ft. SZA',
  'Need to Know - Doja Cat',
  'WAP - Cardi B ft. Megan Thee Stallion',
  'Mood - 24kGoldn ft. Iann Dior',
  'Positions - Ariana Grande',
  'drivers license - Olivia Rodrigo',
  'deja vu - Olivia Rodrigo',
  'traitor - Olivia Rodrigo',
  'All Too Well - Taylor Swift',
  'Willow - Taylor Swift',
  'cardigan - Taylor Swift',
  'august - Taylor Swift',
  'exile - Taylor Swift ft. Bon Iver',
  'the 1 - Taylor Swift',
  'my tears ricochet - Taylor Swift',
  'mirrorball - Taylor Swift',
  'seven - Taylor Swift',
  'this is me trying - Taylor Swift',
  'illicit affairs - Taylor Swift',
  'invisible string - Taylor Swift',
  'mad woman - Taylor Swift',
  'epiphany - Taylor Swift',
  'betty - Taylor Swift',
  'peace - Taylor Swift',
  'hoax - Taylor Swift',
  'the lakes - Taylor Swift',
  'Don\'t Start Now - Dua Lipa',
  'Physical - Dua Lipa',
  'Break My Heart - Dua Lipa',
  'Love Again - Dua Lipa',
  'Pretty Please - Dua Lipa',
  'Hallucinate - Dua Lipa',
  'Cool - Dua Lipa',
  'Future Nostalgia - Dua Lipa',
  'Circles - Post Malone',
  'Sunflower - Post Malone & Swae Lee',
  'Rockstar - Post Malone ft. 21 Savage',
  'Congratulations - Post Malone',
  'Better Now - Post Malone',
  'Wow - Post Malone',
  'Goodbyes - Post Malone ft. Young Thug',
  'I Fall Apart - Post Malone',
  'Psycho - Post Malone ft. Ty Dolla $ign',
  'I Like You - Post Malone ft. Doja Cat',
  'One Right Now - Post Malone & The Weeknd',
  'Can\'t Feel My Face - The Weeknd',
  'The Hills - The Weeknd',
  'Starboy - The Weeknd ft. Daft Punk',
  'I Feel It Coming - The Weeknd ft. Daft Punk',
  'Earned It - The Weeknd',
  'Often - The Weeknd',
  'Wicked Games - The Weeknd',
  'Call Out My Name - The Weeknd',
  'Pray For Me - The Weeknd & Kendrick Lamar',
  'Heartless - The Weeknd',
  'In Your Eyes - The Weeknd',
  'Take My Breath - The Weeknd',
  'Less Than Zero - The Weeknd',
  'Out of Time - The Weeknd',
  'Gasoline - The Weeknd',
  'Die For You - The Weeknd',
  'Popular - The Weeknd ft. Playboi Carti & Madonna',
  'After Hours - The Weeknd',
  'Until I Bleed Out - The Weeknd',
  'Alone Again - The Weeknd',
  'Too Late - The Weeknd',
  'Hardest To Love - The Weeknd'
];

// Generate a random Ethereum wallet address
const generateRandomWallet = () => {
  const chars = '0123456789abcdef';
  let address = '0x';
  for (let i = 0; i < 40; i++) {
    address += chars[Math.floor(Math.random() * chars.length)];
  }
  return address;
};

// Generate multiple photos of the same style (1-5 photos)
// Uses the same seed and image ID but varies size to get same person in different sizes
// This ensures photos look like they're from the same person/style
const generatePhotos = (username, count) => {
  if (count < 1 || count > 5) {
    count = Math.max(1, Math.min(5, count));
  }
  
  // Create a hash from username to get consistent base image ID
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = ((hash << 5) - hash) + username.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Base image ID in range 1-70
  const baseImgId = Math.abs(hash) % 70 + 1;
  
  // Generate photos with same seed and image ID, but vary size slightly
  // This ensures photos are of the same person (same style)
  const sizes = [150, 200, 180, 220, 160]; // Different sizes for variation
  const photos = [];
  for (let i = 0; i < count; i++) {
    // Use same image ID and seed, but vary size to get same person in different sizes
    const size = sizes[i % sizes.length];
    const photoUrl = `https://i.pravatar.cc/${size}?img=${baseImgId}&seed=${username}`;
    photos.push(photoUrl);
  }
  
  return photos;
};

// Generate a deterministic encryption key from wallet address (for populateDatabase)
// This mimics the key generation from signature but uses the address directly
const generateEncryptionKeyFromAddress = (address) => {
  if (!address) {
    throw new Error('Wallet address is required');
  }
  
  // Convert address to bytes (remove 0x prefix and convert hex to bytes)
  const addressBytes = new Uint8Array(
    address.slice(2).match(/.{1,2}/g).map(byte => parseInt(byte, 16))
  );
  
  // Use same salt and info as in generateEncryptionKeyFromSignature
  const saltBytes = new TextEncoder().encode('PeopleTag-encryption-v1');
  const infoBytes = new TextEncoder().encode('PeopletagEncryptionKey');
  
  // Derive 256-bit encryption key using HKDF
  const encryptionKey = hkdf(
    sha256,
    addressBytes,                    // Input key material (address bytes)
    saltBytes,                       // Salt
    infoBytes,                       // Info
    32                              // 32 bytes = 256 bits for AES-256
  );
  
  return encryptionKey;
};

// Generate random profile data
// `nameOrder` (optional) lets populateDatabase prefer unique names within a run.
const generateRandomProfile = (index, nameOrder = null) => {
  // Create a local random function that uses Math.random for true randomness
  const getRandom = () => Math.random();
  
  // Get random name and username (ensure unique usernames)
  const nameIndex = Array.isArray(nameOrder) && nameOrder.length > 0
    ? nameOrder[index % nameOrder.length]
    : Math.floor(getRandom() * randomNames.length);
  // Randomly decide whether to include full name (70% chance to include name)
  const includeName = getRandom() < 0.7;
  const name = includeName ? randomNames[nameIndex] : null;
  const baseUsername = randomUsernames[nameIndex] || `user${index}`;
  const username = `${baseUsername}_${index}`; // Make unique
  
  // Randomly decide whether to include city/location (50% chance to include city)
  const includeCity = getRandom() < 0.5;
  let location = null;
  if (includeCity) {
    // Get random location
    const locationIndex = Math.floor(getRandom() * worldLocations.length);
    location = worldLocations[locationIndex];
  }
  
  // Randomly decide whether to include birth date (50% chance to include birth date)
  const includeBirthDate = getRandom() < 0.5;
  let birthDate = null;
  let age = null;
  if (includeBirthDate) {
    // Generate birth date
    const birthYear = birthYears[Math.floor(getRandom() * birthYears.length)];
    age = 2025 - birthYear;
    const day = Math.floor(getRandom() * 28) + 1;
    const month = Math.floor(getRandom() * 12) + 1;
    birthDate = `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${birthYear}`;
  }
  
  // Random rating (0-5 stars) and survey count
  const rating = Math.floor(getRandom() * 6); // 0-5 stars
  const surveyCount = Math.floor(getRandom() * 1000) + 1; // 1-1000 surveys
  
  // Random number of tags (2-8)
  const numTags = Math.floor(getRandom() * 7) + 2;
  const shuffledTags = [...allTags].sort(() => getRandom() - 0.5);
  const tagsList = shuffledTags.slice(0, numTags);
  
  // Random number of groups (1-5)
  const numGroups = Math.floor(getRandom() * 5) + 1;
  const shuffledGroups = [...allGroups].sort(() => getRandom() - 0.5);
  const groups = shuffledGroups.slice(0, numGroups);
  
  // Random number of social links (1-5)
  const numSocials = Math.floor(getRandom() * 5) + 1;
  const shuffledPlatforms = [...socialPlatforms].sort(() => getRandom() - 0.5);
  const socialLinks = shuffledPlatforms.slice(0, numSocials).map(platform => 
    `${platform}/${username}`
  );
  
  // Random additional personal data (0-3 fields)
  // Filter out fields that are now dedicated fields to avoid duplicates
  // Also filter out "Interests" since interests are already handled via tagsList
  const dedicatedFieldLabels = ['Languages', 'Education', 'Relationship', 'Occupation', 'Interests'];
  const filteredPersonalFields = personalDataFields.filter(field => !dedicatedFieldLabels.includes(field.label));
  const numPersonalFields = Math.floor(getRandom() * 4);
  const shuffledPersonalFields = [...filteredPersonalFields].sort(() => getRandom() - 0.5);
  const additionalPersonalData = shuffledPersonalFields.slice(0, numPersonalFields).map(field => ({
    label: field.label,
    value: field.values[Math.floor(getRandom() * field.values.length)]
  }));
  
  const tags = parseFloat((getRandom() * 2 + 0.5).toFixed(6));
  
  // Randomly decide whether to include email (60% chance to include email)
  const includeEmail = getRandom() < 0.6;
  const email = includeEmail ? `${username}@example.com` : null;
  
  // Randomly decide whether to include avatar (70% chance to include avatar)
  const includeAvatar = getRandom() < 0.7;
  const avatar = includeAvatar ? getAvatarUrl(username) : null;
  
  // Generate 1-5 photos of the same style (if avatar is included, also include photos)
  const includePhotos = includeAvatar && getRandom() < 0.8; // 80% chance if avatar exists
  let photos = [];
  if (includePhotos) {
    const numPhotos = Math.floor(getRandom() * 5) + 1; // 1-5 photos
    photos = generatePhotos(username, numPhotos);
  }
  
  // Randomly decide whether to include bio (60% chance to include bio)
  const includeBio = getRandom() < 0.6;
  const bioTexts = [
    "Hello! I'm passionate about technology and innovation.",
    "Love traveling and exploring new places around the world.",
    "Coffee enthusiast ☕ | Developer | Always learning something new.",
    "Building cool things and connecting with amazing people.",
    "Nature lover 🌲 | Hiking enthusiast | Adventure seeker.",
    "Tech geek | Open source contributor | Problem solver.",
    "Creative mind | Artist | Always inspired by the world around me.",
    "Fitness enthusiast | Healthy lifestyle advocate | Motivator.",
    "Music lover 🎵 | Concert goer | Always discovering new sounds.",
    "Foodie 🍕 | Cooking enthusiast | Love trying new recipes.",
    "Bookworm 📚 | Writer | Storytelling is my passion.",
    "Entrepreneur | Startup founder | Building the future.",
    "Photographer 📸 | Capturing moments | Visual storyteller.",
    "Gamer 🎮 | Streamer | Competitive player.",
    "Yoga instructor | Mindfulness practitioner | Peace seeker.",
    "Engineer | Problem solver | Making things work better.",
    "Designer | Creative thinker | Bringing ideas to life.",
    "Student | Lifelong learner | Knowledge seeker.",
    "Parent | Family first | Creating memories.",
    "Volunteer | Community helper | Making a difference.",
    "Digital nomad exploring the world one city at a time.",
    "Passionate about sustainable living and environmental conservation.",
    "Marathon runner training for my next big race.",
    "Film buff who loves analyzing cinematography and storytelling.",
    "Home chef experimenting with fusion cuisine and local ingredients.",
    "Urban explorer documenting street art and hidden gems.",
    "Meditation teacher helping others find inner peace.",
    "Science communicator making complex topics accessible to everyone.",
    "Dog parent to two amazing rescue pups.",
    "Amateur astronomer stargazing whenever the sky is clear.",
    "Language learner currently studying my third foreign language.",
    "Cycling enthusiast commuting everywhere on two wheels.",
    "Podcast host discussing technology and society.",
    "Minimalist living with less and experiencing more.",
    "Gardener growing vegetables and herbs in my urban space.",
    "Board game collector hosting weekly game nights.",
    "Sourdough baker perfecting my starter and recipes.",
    "History buff fascinated by ancient civilizations.",
    "Surfing beginner catching waves on weekends.",
    "Knitter creating cozy sweaters and scarves for friends.",
    "Rock climber tackling new routes every weekend.",
    "Writer working on my first novel in spare moments.",
    "Dance enthusiast learning salsa and bachata moves.",
    "Beekeeper maintaining hives and harvesting honey.",
    "Mountain biker exploring trails in the wilderness.",
    "Chess player competing in local tournaments.",
    "Bird watcher identifying species in my neighborhood.",
    "Pottery maker throwing clay on the wheel.",
    "Vintage collector hunting for unique finds at markets.",
    "Meditation practitioner starting each day with mindfulness.",
    "Soccer player competing in local leagues on weekends.",
    "Tea connoisseur exploring rare blends from around the world.",
    "Skateboarder practicing tricks at the local skatepark.",
    "Aquarium enthusiast maintaining multiple tanks with exotic fish.",
    "Calligrapher practicing beautiful lettering and typography.",
    "Stand-up comedian performing at open mic nights.",
    "Wildlife photographer capturing animals in their natural habitat.",
    "Fermentation enthusiast making kombucha, kimchi, and sauerkraut.",
    "Motorcycle rider exploring scenic routes on two wheels.",
    "Tattoo artist creating custom designs for clients.",
    "Sewing enthusiast making my own clothes and accessories.",
    "Cave explorer discovering underground formations and passages.",
    "Wine enthusiast learning about different regions and vintages.",
    "Parkour practitioner training to move fluidly through urban spaces.",
    "Astrophotographer capturing stunning images of the night sky.",
    "Origami artist folding intricate paper sculptures.",
    "Ice skater gliding across frozen ponds in winter.",
    "Herbalist studying medicinal plants and natural remedies.",
    "Drone pilot capturing aerial footage of landscapes.",
    "Juggler practicing new patterns and tricks daily.",
    "Scuba diver exploring coral reefs and underwater worlds.",
    "Blacksmith forging tools and decorative metalwork.",
    "Ice climber scaling frozen waterfalls and ice formations.",
    "Bonsai artist cultivating miniature trees with patience.",
    "Kite flyer enjoying windy days at the beach.",
    "Model builder constructing detailed scale replicas.",
    "Caving enthusiast exploring underground cave systems.",
    "Sailing enthusiast navigating waters on my sailboat.",
    "Archery practitioner honing precision and focus.",
    "Geocacher hunting for hidden treasures using GPS coordinates.",
    "Falconer training birds of prey for hunting.",
    "Luthier crafting and repairing stringed instruments.",
    "Mushroom forager identifying edible species in the wild.",
    "Kitesurfer riding waves and wind on my board.",
    "Letterpress printer creating beautiful printed materials.",
    "Freediver exploring depths on a single breath.",
    "Leatherworker crafting bags, wallets, and accessories.",
    "Paraglider soaring through the skies with a wing.",
    "Watchmaker repairing and restoring vintage timepieces.",
    "Canyoneering enthusiast rappelling through narrow canyons.",
    "Soap maker creating natural handmade soaps.",
    "Kayaker paddling through rivers and lakes.",
    "Woodworker crafting furniture and decorative items.",
    "Skydiver experiencing the thrill of freefall.",
    "Candle maker experimenting with scents and wax blends.",
    "Trail runner exploring mountain paths and forest trails.",
    "Jewelry maker designing and crafting unique pieces.",
    "Just here for the vibes.",
    "Living my best life, one adventure at a time. Currently obsessed with learning new skills and meeting interesting people from all walks of life.",
    "🌍✨",
    "Former accountant turned full-time traveler. I left the 9-to-5 behind to explore the world and document my journey. Currently in my 3rd year of nomadic living, and I've never been happier.",
    "Coffee. Code. Repeat.",
    "I believe in the power of small moments. Whether it's watching the sunset, sharing a meal with friends, or reading a good book, life's beauty is in the details.",
    "Building something meaningful.",
    "Passionate educator with 10+ years of experience teaching mathematics to high school students. I love seeing that 'aha!' moment when concepts finally click. When I'm not in the classroom, you'll find me hiking, reading sci-fi novels, or experimenting with new recipes in my kitchen.",
    "Always learning.",
    "Night owl. Book lover. Cat enthusiast. I spend my evenings curled up with a good mystery novel and my two rescue cats. Currently working on my first short story collection.",
    "Making memories, not plans.",
    "Professional musician and part-time music teacher. I play guitar, piano, and violin, and I'm always looking for new collaborators. My music blends jazz, folk, and electronic elements. Let's create something beautiful together.",
    "Just vibing.",
    "Retired early to pursue my passion for sustainable farming. I run a small organic farm where I grow vegetables, raise chickens, and teach workshops on permaculture. Living simply and intentionally.",
    "Work in progress.",
    "I'm a data analyst by day and a fantasy novelist by night. My debut novel is coming out next year! I love world-building, character development, and exploring themes of identity and belonging in my writing.",
    "Here for a good time.",
    "Marine biologist studying coral reef ecosystems. I spend months at a time on research vessels in the Pacific, diving and collecting data. When I'm on land, I'm usually at the beach or planning my next expedition.",
    "Living intentionally.",
    "Small business owner running a local bakery. I wake up at 3 AM every day to bake fresh bread, pastries, and cakes. It's hard work, but seeing customers' smiles makes it all worth it. Community is everything.",
    "Just trying to figure it out.",
    "I'm a software engineer who loves solving complex problems and building products that make people's lives easier. In my free time, I contribute to open-source projects, play board games with friends, and go on long bike rides through the city.",
    "Making it up as I go.",
    "Therapist specializing in anxiety and depression. I help people navigate life's challenges and find their inner strength. Self-care is important to me—I practice yoga daily and maintain a regular meditation practice.",
    "Here to connect.",
    "I'm a freelance graphic designer working with startups and small businesses. I love creating visual identities that tell a story. When I'm not designing, I'm usually at art galleries, trying new restaurants, or traveling to find inspiration.",
    "Living my truth.",
    "Emergency room nurse with 15 years of experience. My job is intense, but I find it incredibly rewarding. I'm passionate about patient advocacy and mental health awareness. Outside of work, I enjoy rock climbing and cooking elaborate meals for friends.",
    "Just being me.",
    "I'm a stay-at-home parent to three amazing kids. My days are filled with school runs, playdates, and trying to keep the house somewhat organized. It's chaotic and exhausting, but I wouldn't trade it for anything. Coffee is my lifeline.",
    "Trying to be better every day.",
    "Architect designing sustainable buildings for the future. I believe architecture should harmonize with nature, not dominate it. My current project is a net-zero energy housing development. I also love sketching, photography, and exploring cities on foot.",
    "Here to learn and grow.",
    "I'm a professional athlete competing in triathlons. Training takes up most of my time, but I also enjoy reading, cooking, and spending time with my family. My goal is to qualify for the Olympics next year.",
    "Living authentically.",
    "Social worker helping families navigate the foster care system. It's emotionally challenging work, but incredibly meaningful. I'm also a foster parent myself. In my spare time, I garden, read memoirs, and volunteer at animal shelters.",
    "Just here.",
    "I'm a research scientist studying climate change impacts on agriculture. My work takes me to field sites around the world. I'm passionate about finding solutions to feed a growing population while protecting our planet. I also love hiking, birdwatching, and stargazing.",
    "Making the most of every moment.",
    "Chef and restaurant owner. I trained in France and worked in Michelin-starred restaurants before opening my own place. Food is my art, and I love creating dishes that tell a story. I'm also passionate about supporting local farmers and sustainable agriculture.",
    "Here to make a difference.",
    "I'm a journalist covering environmental issues and social justice. I've reported from over 30 countries and won several awards for my investigative work. Storytelling is my passion, and I believe in the power of journalism to create change.",
    "Living life on my terms.",
    "Veterinarian specializing in exotic animals. I treat everything from parrots to reptiles to small mammals. It's never boring! I also volunteer at wildlife rehabilitation centers and have two dogs, three cats, and a parrot at home.",
    "Just doing my thing.",
    "I'm a mechanical engineer working on renewable energy projects. I design wind turbines and solar panel systems. Climate change is the defining challenge of our time, and I'm proud to be part of the solution. I also enjoy woodworking and building furniture in my garage workshop.",
    "Here to explore and discover.",
    "Professional photographer specializing in wedding and portrait photography. I've shot over 200 weddings and countless portrait sessions. I love capturing authentic moments and emotions. When I'm not behind the camera, I'm usually traveling or spending time with my family.",
    "Living with purpose.",
    "I'm a high school teacher and debate coach. I teach history and government, and I'm passionate about helping students become critical thinkers and engaged citizens. I also coach the debate team, which has won state championships three years in a row.",
    "Just trying to stay positive.",
    "Retired military officer now working as a consultant. I served for 20 years and deployed to multiple countries. Now I help veterans transition to civilian life and work with companies on leadership development. I enjoy fishing, reading military history, and spending time with my grandchildren.",
    "Here to share and connect.",
    "I'm a yoga instructor and wellness coach. I teach classes at several studios and offer private sessions. My approach combines physical practice with mindfulness and self-compassion. I'm also studying to become a certified nutritionist.",
    "Living in the moment.",
    "Small-town librarian who loves connecting people with books. I've been working at the same library for 25 years and know most of our regular patrons by name. I'm passionate about literacy, community programs, and preserving local history. I also write poetry in my spare time."
  ];
  const bio = includeBio ? bioTexts[Math.floor(getRandom() * bioTexts.length)] : null;
  
  // Randomly decide whether to include tags (70% chance to include tags)
  const includeTags = getRandom() < 0.7;
  const finalTagsList = includeTags ? tagsList : [];
  
  // Randomly decide whether to include social links (60% chance to include socials)
  const includeSocials = getRandom() < 0.6;
  const finalSocialLinks = includeSocials ? socialLinks : [];
  
  // Randomly decide whether to include relationship status (50% chance)
  const includeRelationship = getRandom() < 0.5;
  const relationshipStatuses = [
    'Single',
    'In a relationship',
    'Married',
    'Divorced',
    'Widowed',
    'Separated',
    'Engaged',
    'It\'s complicated',
    'Open relationship',
    'Polyamorous',
    'Dating',
    'Seeing someone',
    'Prefer not to say',
    'Not specified',
    'Single and looking',
    'Single and not looking',
    'In a long-distance relationship',
    'Recently single',
    'Committed relationship',
    'Casually dating',
    'Living together',
    'Domestic partnership',
    'Civil union',
    'Common-law marriage',
    'Annulled',
    'Never married',
    'Divorced and dating',
    'Widowed and dating',
    'Separated and dating',
    'Partnered',
    'In a situationship',
    'Taking a break',
    'Not dating',
    'Exploring options',
    'Available',
    'Unavailable',
    'Complicated',
    'Single parent',
    'Married with kids',
    'Married without kids',
    'Happily married',
    'Looking for love',
    'Not looking for love',
    'Content alone',
    'Seeking companionship',
    'In an open marriage',
    'Separated but dating',
    'Widowed but dating',
    'Divorced but dating'
  ];
  const relationshipStatus = includeRelationship ? relationshipStatuses[Math.floor(getRandom() * relationshipStatuses.length)] : null;
  
  // Randomly decide whether to include looking for (50% chance)
  const includeLookingFor = getRandom() < 0.5;
  const lookingForOptions = [
    'Friends',
    'Networking',
    'Dating',
    'Business',
    'Collaboration',
    'Mentorship',
    'Travel companions',
    'Activity partners',
    'Study groups',
    'Professional connections',
    'Romantic relationship',
    'Casual dating',
    'Serious relationship',
    'Life partner',
    'Business partners',
    'Co-founders',
    'Investors',
    'Clients',
    'Customers',
    'Team members',
    'Hiking buddies',
    'Gym partners',
    'Running partners',
    'Photography partners',
    'Music collaborators',
    'Art collaborators',
    'Writing partners',
    'Gaming buddies',
    'Book club members',
    'Language exchange',
    'Cultural exchange',
    'Volunteer partners',
    'Event organizers',
    'Community builders',
    'Startup founders',
    'Tech enthusiasts',
    'Creative professionals',
    'Fitness enthusiasts',
    'Food lovers',
    'Adventure seekers',
    'Learning partners',
    'Skill sharing',
    'Knowledge exchange',
    'Support groups',
    'Interest groups',
    'Hobby groups',
    'Sports teams',
    'Music bands',
    'Theater groups',
    'Film projects'
  ];
  const lookingFor = includeLookingFor ? lookingForOptions[Math.floor(getRandom() * lookingForOptions.length)] : null;
  
  // Randomly decide whether to include languages (50% chance)
  const includeLanguages = getRandom() < 0.5;
  
  // Map cities to likely languages (if city is specified, prefer these languages)
  const cityLanguageMap = {
    // English-speaking cities
    'London': ['English', 'French', 'German', 'Spanish'],
    'New York': ['English', 'Spanish', 'Chinese', 'French'],
    'Los Angeles': ['English', 'Spanish', 'Chinese', 'Korean'],
    'San Francisco': ['English', 'Chinese', 'Spanish', 'Japanese'],
    'Chicago': ['English', 'Spanish', 'Polish', 'Chinese'],
    'Miami': ['English', 'Spanish', 'Portuguese', 'French'],
    'Toronto': ['English', 'French', 'Chinese', 'Italian'],
    'Vancouver': ['English', 'Chinese', 'Punjabi', 'French'],
    'Boston': ['English', 'Spanish', 'Chinese', 'Portuguese'],
    'Seattle': ['English', 'Chinese', 'Spanish', 'Vietnamese'],
    'Las Vegas': ['English', 'Spanish', 'Chinese', 'Tagalog'],
    'Denver': ['English', 'Spanish', 'Vietnamese', 'Russian'],
    'Portland': ['English', 'Spanish', 'Vietnamese', 'Russian'],
    'Sydney': ['English', 'Chinese', 'Arabic', 'Greek'],
    'Melbourne': ['English', 'Chinese', 'Greek', 'Italian'],
    'Auckland': ['English', 'Māori', 'Samoan', 'Chinese'],
    'Perth': ['English', 'Chinese', 'Italian', 'Greek'],
    'Brisbane': ['English', 'Chinese', 'Vietnamese', 'Italian'],
    'Adelaide': ['English', 'Greek', 'Italian', 'German'],
    'Wellington': ['English', 'Māori', 'Samoan', 'Chinese'],
    'Christchurch': ['English', 'Māori', 'Chinese', 'Japanese'],
    'Dublin': ['English', 'Irish', 'Polish', 'Lithuanian'],
    'Edinburgh': ['English', 'Polish', 'Chinese', 'Urdu'],
    'Singapore': ['English', 'Mandarin', 'Malay', 'Tamil'],
    'Hong Kong': ['Cantonese', 'English', 'Mandarin', 'Tagalog'],
    
    // Spanish-speaking cities
    'Madrid': ['Spanish', 'English', 'French', 'Arabic'],
    'Barcelona': ['Spanish', 'Catalan', 'English', 'French'],
    'Valencia': ['Spanish', 'Catalan', 'English', 'French'],
    'Seville': ['Spanish', 'English', 'French', 'German'],
    'Granada': ['Spanish', 'English', 'French', 'Arabic'],
    'Mexico City': ['Spanish', 'English', 'Nahuatl', 'French'],
    'Buenos Aires': ['Spanish', 'English', 'Italian', 'Portuguese'],
    'São Paulo': ['Portuguese', 'Spanish', 'English', 'Italian'],
    'Rio de Janeiro': ['Portuguese', 'English', 'Spanish', 'French'],
    'Lima': ['Spanish', 'Quechua', 'English', 'Portuguese'],
    'Bogotá': ['Spanish', 'English', 'French', 'Portuguese'],
    'Medellín': ['Spanish', 'English', 'French', 'Portuguese'],
    'Cartagena': ['Spanish', 'English', 'French', 'Portuguese'],
    'Santiago': ['Spanish', 'English', 'German', 'Italian'],
    'Quito': ['Spanish', 'Quechua', 'English', 'French'],
    'Montevideo': ['Spanish', 'English', 'Portuguese', 'Italian'],
    'Guadalajara': ['Spanish', 'English', 'French', 'Italian'],
    'Cancún': ['Spanish', 'English', 'French', 'German'],
    'Tijuana': ['Spanish', 'English', 'Chinese', 'Japanese'],
    'Salvador': ['Portuguese', 'English', 'Spanish', 'French'],
    'Brasília': ['Portuguese', 'English', 'Spanish', 'French'],
    'Curitiba': ['Portuguese', 'English', 'Spanish', 'Italian'],
    'Recife': ['Portuguese', 'English', 'Spanish', 'French'],
    'Fortaleza': ['Portuguese', 'English', 'Spanish', 'French'],
    
    // French-speaking cities
    'Paris': ['French', 'English', 'Arabic', 'Spanish'],
    'Montreal': ['French', 'English', 'Spanish', 'Italian'],
    'Lyon': ['French', 'English', 'Arabic', 'Italian'],
    'Marseille': ['French', 'English', 'Arabic', 'Italian'],
    'Nice': ['French', 'English', 'Italian', 'Spanish'],
    'Geneva': ['French', 'English', 'German', 'Italian'],
    'Brussels': ['French', 'Dutch', 'English', 'German'],
    'Casablanca': ['Arabic', 'French', 'Berber', 'English'],
    'Marrakech': ['Arabic', 'French', 'Berber', 'English'],
    'Tunis': ['Arabic', 'French', 'Berber', 'English'],
    'Algiers': ['Arabic', 'French', 'Berber', 'English'],
    'Dakar': ['French', 'Wolof', 'English', 'Arabic'],
    
    // German-speaking cities
    'Berlin': ['German', 'English', 'Turkish', 'Polish'],
    'Munich': ['German', 'English', 'Turkish', 'Italian'],
    'Frankfurt': ['German', 'English', 'Turkish', 'Italian'],
    'Hamburg': ['German', 'English', 'Turkish', 'Polish'],
    'Cologne': ['German', 'English', 'Turkish', 'Italian'],
    'Vienna': ['German', 'English', 'Turkish', 'Serbian'],
    'Zurich': ['German', 'French', 'Italian', 'English'],
    
    // Italian-speaking cities
    'Rome': ['Italian', 'English', 'Spanish', 'French'],
    'Florence': ['Italian', 'English', 'Spanish', 'French'],
    'Venice': ['Italian', 'English', 'Spanish', 'German'],
    'Milan': ['Italian', 'English', 'Spanish', 'French'],
    'Naples': ['Italian', 'English', 'Spanish', 'French'],
    
    // Portuguese-speaking cities (already covered in Spanish section)
    
    // Dutch-speaking cities
    'Amsterdam': ['Dutch', 'English', 'Arabic', 'Turkish'],
    
    // Scandinavian cities
    'Stockholm': ['Swedish', 'English', 'Arabic', 'Finnish'],
    'Copenhagen': ['Danish', 'English', 'Arabic', 'German'],
    'Oslo': ['Norwegian', 'English', 'Urdu', 'Polish'],
    'Helsinki': ['Finnish', 'Swedish', 'English', 'Russian'],
    'Reykjavik': ['Icelandic', 'English', 'Polish', 'Lithuanian'],
    
    // Eastern European cities
    'Prague': ['Czech', 'English', 'Slovak', 'German'],
    'Budapest': ['Hungarian', 'English', 'German', 'Romanian'],
    'Warsaw': ['Polish', 'English', 'German', 'Russian'],
    'Krakow': ['Polish', 'English', 'German', 'Ukrainian'],
    'Bucharest': ['Romanian', 'English', 'Hungarian', 'German'],
    'Sofia': ['Bulgarian', 'English', 'Turkish', 'Russian'],
    'Zagreb': ['Croatian', 'English', 'Serbian', 'German'],
    'Belgrade': ['Serbian', 'English', 'Croatian', 'Hungarian'],
    'Kyiv': ['Ukrainian', 'Russian', 'English', 'Polish'],
    'Moscow': ['Russian', 'English', 'Ukrainian', 'Tatar'],
    
    // Greek cities
    'Athens': ['Greek', 'English', 'Albanian', 'Bulgarian'],
    
    // Turkish cities
    'Istanbul': ['Turkish', 'English', 'Kurdish', 'Arabic'],
    
    // Middle Eastern cities
    'Dubai': ['Arabic', 'English', 'Hindi', 'Urdu'],
    'Abu Dhabi': ['Arabic', 'English', 'Hindi', 'Urdu'],
    'Doha': ['Arabic', 'English', 'Hindi', 'Urdu'],
    'Kuwait City': ['Arabic', 'English', 'Hindi', 'Urdu'],
    'Riyadh': ['Arabic', 'English', 'Hindi', 'Urdu'],
    'Jeddah': ['Arabic', 'English', 'Hindi', 'Urdu'],
    'Muscat': ['Arabic', 'English', 'Hindi', 'Urdu'],
    'Beirut': ['Arabic', 'French', 'English', 'Armenian'],
    'Amman': ['Arabic', 'English', 'Armenian', 'Circassian'],
    'Jerusalem': ['Hebrew', 'Arabic', 'English', 'Russian'],
    'Tel Aviv': ['Hebrew', 'Arabic', 'English', 'Russian'],
    'Cairo': ['Arabic', 'English', 'French', 'Italian'],
    'Alexandria': ['Arabic', 'English', 'French', 'Greek'],
    
    // Asian cities
    'Tokyo': ['Japanese', 'English', 'Chinese', 'Korean'],
    'Osaka': ['Japanese', 'English', 'Chinese', 'Korean'],
    'Kyoto': ['Japanese', 'English', 'Chinese', 'Korean'],
    'Yokohama': ['Japanese', 'English', 'Chinese', 'Korean'],
    'Seoul': ['Korean', 'English', 'Chinese', 'Japanese'],
    'Beijing': ['Mandarin', 'English', 'Cantonese', 'Mongolian'],
    'Shanghai': ['Mandarin', 'English', 'Shanghainese', 'Cantonese'],
    'Hong Kong': ['Cantonese', 'English', 'Mandarin', 'Tagalog'],
    'Taipei': ['Mandarin', 'English', 'Taiwanese', 'Hakka'],
    'Bangkok': ['Thai', 'English', 'Chinese', 'Lao'],
    'Phuket': ['Thai', 'English', 'Chinese', 'Malay'],
    'Chiang Mai': ['Thai', 'English', 'Lao', 'Chinese'],
    'Ho Chi Minh City': ['Vietnamese', 'English', 'Chinese', 'French'],
    'Hanoi': ['Vietnamese', 'English', 'Chinese', 'French'],
    'Kuala Lumpur': ['Malay', 'English', 'Mandarin', 'Tamil'],
    'Penang': ['Malay', 'English', 'Mandarin', 'Tamil'],
    'Jakarta': ['Indonesian', 'English', 'Javanese', 'Sundanese'],
    'Yogyakarta': ['Indonesian', 'Javanese', 'English', 'Sundanese'],
    'Bandung': ['Indonesian', 'Sundanese', 'English', 'Javanese'],
    'Surabaya': ['Indonesian', 'Javanese', 'English', 'Madurese'],
    'Manila': ['Tagalog', 'English', 'Cebuano', 'Ilocano'],
    'Mumbai': ['Hindi', 'English', 'Marathi', 'Gujarati'],
    'Delhi': ['Hindi', 'English', 'Punjabi', 'Urdu'],
    'Bangalore': ['Kannada', 'English', 'Hindi', 'Tamil'],
    'Dhaka': ['Bengali', 'English', 'Urdu', 'Hindi'],
    'Lahore': ['Urdu', 'Punjabi', 'English', 'Pashto'],
    'Karachi': ['Urdu', 'English', 'Sindhi', 'Punjabi'],
    'Islamabad': ['Urdu', 'English', 'Punjabi', 'Pashto'],
    'Colombo': ['Sinhala', 'Tamil', 'English', 'Malay'],
    'Kathmandu': ['Nepali', 'English', 'Newari', 'Maithili'],
    
    // Central Asian cities
    'Tashkent': ['Uzbek', 'Russian', 'English', 'Tajik'],
    'Almaty': ['Kazakh', 'Russian', 'English', 'Uyghur'],
    'Bishkek': ['Kyrgyz', 'Russian', 'English', 'Uzbek'],
    'Dushanbe': ['Tajik', 'Russian', 'English', 'Uzbek'],
    'Ashgabat': ['Turkmen', 'Russian', 'English', 'Uzbek'],
    'Baku': ['Azerbaijani', 'Russian', 'English', 'Turkish'],
    'Yerevan': ['Armenian', 'Russian', 'English', 'Kurdish'],
    'Tbilisi': ['Georgian', 'Russian', 'English', 'Armenian'],
    'Ulaanbaatar': ['Mongolian', 'English', 'Russian', 'Chinese'],
    
    // African cities
    'Johannesburg': ['English', 'Afrikaans', 'Zulu', 'Xhosa'],
    'Cape Town': ['English', 'Afrikaans', 'Xhosa', 'Zulu'],
    'Nairobi': ['English', 'Swahili', 'Kikuyu', 'Luhya'],
    'Lagos': ['English', 'Yoruba', 'Igbo', 'Hausa'],
    'Accra': ['English', 'Akan', 'Ga', 'Ewe'],
  };
  
  const languageOptions = [
    'English',
    'Spanish',
    'French',
    'German',
    'Italian',
    'Portuguese',
    'Russian',
    'Chinese',
    'Japanese',
    'Korean',
    'Arabic',
    'Hindi',
    'Dutch',
    'Swedish',
    'Norwegian',
    'Danish',
    'Polish',
    'Turkish',
    'Greek',
    'Hebrew',
    'Mandarin',
    'Cantonese',
    'Vietnamese',
    'Thai',
    'Indonesian',
    'Malay',
    'Tagalog',
    'Bengali',
    'Urdu',
    'Persian',
    'Czech',
    'Hungarian',
    'Romanian',
    'Bulgarian',
    'Croatian',
    'Serbian',
    'Slovak',
    'Slovenian',
    'Finnish',
    'Estonian',
    'Latvian',
    'Lithuanian',
    'Icelandic',
    'Irish',
    'Welsh',
    'Catalan',
    'Basque',
    'Galician',
    'Swahili',
    'Zulu',
    'Afrikaans',
    'Amharic',
    'Tamil',
    'Telugu',
    'Marathi',
    'Gujarati',
    'Punjabi',
    'Malayalam',
    'Kannada',
    'Sinhala',
    'Nepali',
    'Burmese',
    'Khmer',
    'Lao',
    'Mongolian',
    'Georgian',
    'Armenian',
    'Azerbaijani',
    'Kazakh',
    'Ukrainian',
    'Belarusian',
    'Macedonian',
    'Albanian',
    'Bosnian',
    'Montenegrin',
    'Maltese',
    'Luxembourgish',
    'Esperanto',
    'Sign Language',
    'ASL',
    'BSL',
    'Multilingual',
    'Hausa',
    'Yoruba',
    'Igbo',
    'Javanese',
    'Sundanese',
    'Filipino',
    'Cebuano',
    'Ilocano',
    'Hiligaynon',
    'Waray',
    'Kapampangan',
    'Pangasinan',
    'Bicolano',
    'Maranao',
    'Maguindanao',
    'Tausug',
    'Chavacano',
    'Aklanon',
    'Kinaray-a',
    'Surigaonon',
    'Romblomanon',
    'Masbateño',
    'Cuyonon',
    'Bolinao',
    'Ibanag',
    'Ivatan',
    'Yakan',
    'Tboli',
    'Manobo',
    'Subanen',
    'Tiruray',
    'Blaan',
    'Mangyan',
    'Tagbanwa',
    'Palawano',
    'Batak'
  ];
  
  // Select languages based on city if specified, otherwise random
  let selectedLanguages = [];
  if (includeLanguages && location && location.city && cityLanguageMap[location.city]) {
    // City is specified - prefer languages for that city
    // Filter city languages to only include those that exist in languageOptions
    const cityLanguages = cityLanguageMap[location.city].filter(lang => languageOptions.includes(lang));
    const numLanguages = Math.floor(getRandom() * 4) + 1;
    
    // 70% chance to include at least one city-specific language (if any are available)
    const includeCityLanguage = getRandom() < 0.7 && cityLanguages.length > 0;
    
    if (includeCityLanguage) {
      // Select 1-2 languages from city-specific list
      const numCityLanguages = Math.min(numLanguages, Math.max(1, Math.floor(getRandom() * 2) + 1), cityLanguages.length);
      const shuffledCityLanguages = [...cityLanguages].sort(() => getRandom() - 0.5);
      selectedLanguages = shuffledCityLanguages.slice(0, numCityLanguages);
      
      // Fill remaining slots with random languages from full list
      const remaining = numLanguages - selectedLanguages.length;
      if (remaining > 0) {
        const availableLanguages = languageOptions.filter(lang => !selectedLanguages.includes(lang));
        const shuffledAll = [...availableLanguages].sort(() => getRandom() - 0.5);
        selectedLanguages = [...selectedLanguages, ...shuffledAll.slice(0, remaining)];
      }
    } else {
      // 30% chance to select completely random languages
      const shuffledLanguages = [...languageOptions].sort(() => getRandom() - 0.5);
      selectedLanguages = shuffledLanguages.slice(0, numLanguages);
    }
  } else {
    // No city specified - random selection
    const numLanguages = includeLanguages ? Math.floor(getRandom() * 4) + 1 : 0;
    const shuffledLanguages = [...languageOptions].sort(() => getRandom() - 0.5);
    selectedLanguages = shuffledLanguages.slice(0, numLanguages);
  }
  
  const languages = selectedLanguages.length > 0 ? selectedLanguages.join(', ') : null;
  
  // Randomly decide whether to include education (50% chance)
  const includeEducation = getRandom() < 0.5;
  const educationOptions = [
    'High School',
    'High School Diploma',
    'GED',
    'Associate Degree',
    'Associate of Arts',
    'Associate of Science',
    'Bachelor\'s Degree',
    'Bachelor of Arts',
    'Bachelor of Science',
    'Bachelor of Engineering',
    'Bachelor of Business Administration',
    'Bachelor of Fine Arts',
    'Master\'s Degree',
    'Master of Arts',
    'Master of Science',
    'Master of Business Administration',
    'Master of Engineering',
    'Master of Fine Arts',
    'Master of Public Administration',
    'Master of Public Health',
    'Master of Education',
    'Master of Laws',
    'PhD',
    'Doctor of Philosophy',
    'Doctor of Medicine',
    'Doctor of Law',
    'Doctor of Education',
    'Doctor of Business Administration',
    'JD',
    'MD',
    'DDS',
    'DVM',
    'Professional Certificate',
    'Technical Certificate',
    'Industry Certification',
    'Self-taught',
    'Trade School',
    'Vocational School',
    'Online Courses',
    'Coding Bootcamp',
    'Online Degree',
    'Continuing Education',
    'Professional Development',
    'Apprenticeship',
    'Internship Program',
    'Military Training',
    'On-the-job Training',
    'Workshop Series',
    'Seminar Training',
    'Certificate Program',
    'Diploma Program',
    'Postgraduate Certificate',
    'Postgraduate Diploma',
    'Foundation Degree',
    'HNC/HND',
    'International Baccalaureate',
    'Some College',
    'Currently Studying',
    'Exchange Program',
    'Study Abroad',
    'Dual Degree',
    'Joint Degree',
    'Distance Learning',
    'Correspondence Course',
    'Evening Classes',
    'Part-time Study',
    'Full-time Study',
    'Executive Education',
    'Corporate Training',
    'Professional Training',
    'Skills Training',
    'Career Training',
    'Adult Education',
    'Lifelong Learning',
    'Open University',
    'University Extension',
    'Community College',
    'Junior College',
    'Technical Institute',
    'Polytechnic',
    'Conservatory',
    'Academy',
    'Seminary',
    'Yeshiva',
    'Madrasa',
    'Monastery School',
    'Homeschooling',
    'Unschooling',
    'Alternative Education',
    'Montessori',
    'Waldorf',
    'Steiner',
    'Reggio Emilia',
    'IB Program',
    'AP Program',
    'A-Levels',
    'GCSE',
    'O-Levels',
    'Matriculation',
    'Abitur',
    'Baccalauréat',
    'Maturità',
    'Bachillerato',
    'Leaving Certificate',
    'High School Equivalency',
    'College Prep',
    'Advanced Placement',
    'Honors Program',
    'Gifted Program',
    'Special Education',
    'Remedial Education',
    'Basic Education',
    'Primary Education',
    'Secondary Education',
    'Tertiary Education',
    'Higher Education',
    'Graduate Education',
    'Postgraduate Education',
    'Doctoral Education',
    'Postdoctoral',
    'Research Fellowship',
    'Teaching Fellowship',
    'Scholarship Program',
    'Grant Program',
    'Fellowship Program',
    'Internship',
    'Externship',
    'Residency',
    'Clinical Rotation',
    'Field Work',
    'Practicum',
    'Student Teaching',
    'Co-op Program',
    'Work-study',
    'Service Learning',
    'Volunteer Training',
    'Leadership Program',
    'Mentorship Program',
    'Tutoring',
    'Test Prep',
    'Exam Preparation',
    'Licensure',
    'Certification',
    'Recertification',
    'Continuing Medical Education',
    'Continuing Legal Education',
    'Professional License',
    'State License',
    'Federal License',
    'Industry License',
    'Trade License',
    'Business License',
    'Driver\'s License'
  ];
  const education = includeEducation ? educationOptions[Math.floor(getRandom() * educationOptions.length)] : null;
  
  // Randomly decide whether to include occupation (60% chance)
  const includeOccupation = getRandom() < 0.6;
  const occupationOptions = [
    'Software Engineer',
    'Designer',
    'Teacher',
    'Doctor',
    'Lawyer',
    'Consultant',
    'Marketing Manager',
    'Product Manager',
    'Data Scientist',
    'Entrepreneur',
    'Artist',
    'Writer',
    'Photographer',
    'Chef',
    'Architect',
    'Engineer',
    'Nurse',
    'Accountant',
    'Sales Manager',
    'Student',
    'Researcher',
    'Developer',
    'Business Analyst',
    'Project Manager',
    'Freelancer',
    'Web Developer',
    'Mobile Developer',
    'UI/UX Designer',
    'Graphic Designer',
    'Illustrator',
    'Animator',
    'Video Editor',
    'Content Creator',
    'Social Media Manager',
    'Digital Marketer',
    'SEO Specialist',
    'Copywriter',
    'Journalist',
    'Editor',
    'Translator',
    'Interpreter',
    'Therapist',
    'Psychologist',
    'Counselor',
    'Dentist',
    'Veterinarian',
    'Pharmacist',
    'Physical Therapist',
    'Occupational Therapist',
    'Radiologist',
    'Electrician',
    'Plumber',
    'Carpenter',
    'Mechanic',
    'Firefighter',
    'Police Officer',
    'Pilot',
    'Flight Attendant',
    'Air Traffic Controller',
    'Marine Biologist',
    'Geologist',
    'Astronomer',
    'Chemist',
    'Biologist',
    'Physicist',
    'Mathematician',
    'Statistician',
    'Economist',
    'Financial Advisor',
    'Investment Banker',
    'Real Estate Agent',
    'Insurance Agent',
    'Human Resources Manager',
    'Recruiter',
    'Operations Manager',
    'Supply Chain Manager',
    'Logistics Coordinator',
    'Warehouse Manager',
    'Truck Driver',
    'Delivery Driver',
    'Postal Worker',
    'Librarian',
    'Museum Curator',
    'Archivist',
    'Historian',
    'Anthropologist',
    'Sociologist',
    'Political Scientist',
    'Urban Planner',
    'Civil Engineer',
    'Mechanical Engineer',
    'Electrical Engineer',
    'Chemical Engineer',
    'Aerospace Engineer',
    'Biomedical Engineer',
    'Environmental Engineer',
    'Industrial Engineer',
    'Fashion Designer',
    'Interior Designer',
    'Landscape Architect'
  ];
  const occupation = includeOccupation ? occupationOptions[Math.floor(getRandom() * occupationOptions.length)] : null;
  
  // Randomly decide whether to include job title/role (50% chance)
  const includeJobTitle = getRandom() < 0.5;
  const jobTitleOptions = [
    'Senior Developer',
    'Lead Engineer',
    'Manager',
    'Director',
    'VP',
    'CEO',
    'CTO',
    'CFO',
    'Head of Design',
    'Principal Architect',
    'Senior Designer',
    'Creative Director',
    'Product Lead',
    'Engineering Manager',
    'Technical Lead',
    'Team Lead',
    'Senior Analyst',
    'Associate',
    'Junior Developer',
    'Intern',
    'Staff Engineer',
    'Principal Engineer',
    'Distinguished Engineer',
    'VP of Engineering',
    'VP of Product',
    'VP of Marketing',
    'VP of Sales',
    'Chief Marketing Officer',
    'Chief Operating Officer',
    'Chief Product Officer',
    'Chief Revenue Officer',
    'Chief Technology Officer',
    'Chief Financial Officer',
    'Chief Executive Officer',
    'Founder',
    'Co-founder',
    'Head of Engineering',
    'Head of Product',
    'Head of Marketing',
    'Head of Sales',
    'Head of Operations',
    'Head of HR',
    'Head of Finance',
    'Senior Manager',
    'General Manager',
    'Regional Manager',
    'Department Head',
    'Section Head',
    'Unit Manager',
    'Program Manager'
  ];
  const role = includeJobTitle ? jobTitleOptions[Math.floor(getRandom() * jobTitleOptions.length)] : null;
  
  // Randomly decide whether to include music (70% chance to include music)
  const includeMusic = getRandom() < 0.7;
  let musicList = [];
  if (includeMusic) {
    // Random number of songs (0-5)
    const numSongs = Math.floor(getRandom() * 6); // 0-5 songs
    if (numSongs > 0) {
      // Shuffle and select random songs
      const shuffledSongs = [...popularSongs].sort(() => getRandom() - 0.5);
      musicList = shuffledSongs.slice(0, numSongs);
    }
  }
  
  return {
    name,
    username,
    email,
    rating,
    surveyCount,
    tags,
    tagsList: finalTagsList,
    groups,
    socialLinks: finalSocialLinks,
    birthDate,
    age,
    city: location ? location.city : null,
    location: location ? {
      name: location.name,
      city: location.city,
      lat: location.lat,
      lng: location.lng
    } : null,
    avatar, // Generate random avatar based on username (or null if not included)
    photos, // Array of 1-5 photos of the same style (or empty array)
    bio, // Random bio text or null
    relationshipStatus, // Random relationship status or null
    lookingFor, // Random looking for or null
    languages, // Random languages or null
    education, // Random education or null
    occupation, // Random occupation or null
    role, // Random job title or null
    musicList, // Random 0-5 popular songs
    additionalPersonalData,
    createdAt: new Date(),
    updatedAt: new Date()
  };
};

// Helper function to process profiles in parallel with concurrency limit
const processInBatches = async (items, batchSize, processor) => {
  const results = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchNum = Math.floor(i / batchSize) + 1;
    const totalBatches = Math.ceil(items.length / batchSize);
    console.log(`  Processing batch ${batchNum}/${totalBatches} (${batch.length} items)...`);
    try {
      const batchResults = await Promise.all(batch.map(processor));
      results.push(...batchResults);
      console.log(`  ✓ Batch ${batchNum}/${totalBatches} completed`);
    } catch (error) {
      console.error(`  ✗ Error in batch ${batchNum}:`, error);
      // Continue with other batches even if one fails
      const errorResults = batch.map((item, idx) => ({
        ...item,
        success: false,
        error: error.message
      }));
      results.push(...errorResults);
    }
  }
  return results;
};

// Pre-fetch all profile IDs in one transaction
const getNextProfileIds = async (count) => {
  const counterRef = doc(db, 'counters', 'profile_id');
  const ids = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(counterRef);
    const current = snap.exists() ? (snap.data().next || 1) : 1;
    const ids = [];
    for (let i = 0; i < count; i++) {
      ids.push(current + i);
    }
    transaction.set(counterRef, { next: current + count }, { merge: true });
    return ids;
  });
  return ids;
};

// Main function to populate database
// Note: If you see "ERR_BLOCKED_BY_CLIENT" errors, this is typically caused by browser
// extensions (ad blockers, privacy tools) blocking Firestore requests. Consider:
// 1. Disabling browser extensions temporarily
// 2. Adding firestore.googleapis.com to your extension's allowlist
// 3. Using a different browser or incognito mode
export const populateDatabase = async (progressCallback, count = 50) => {
  console.log(`Starting to populate database with ${count} random profiles...`);
  const startTime = Date.now();
  
  try {
    if (progressCallback) {
      progressCallback({ phase: 'starting', progress: 0, total: count });
    }
  } catch (e) {
    // Ignore callback errors
  }
  
  const profiles = [];
  const errors = [];
  // Track city usage to add offsets for duplicates
  const cityUsage = new Map(); // city -> array of {lat, lng} used
  // Prefer unique names during generation by shuffling the name indices once.
  // This prevents repeating the same few names even when `count` gets large.
  const nameOrder = Array.from({ length: randomNames.length }, (_, i) => i)
    .sort(() => Math.random() - 0.5);
  const stats = {
    profilesWithCity: 0,
    profilesWithoutCity: 0,
    profilesWithName: 0,
    profilesWithoutName: 0,
    profilesWithBirthDate: 0,
    profilesWithoutBirthDate: 0,
    profilesWithWallet: 0,
    profilesWithoutWallet: 0,
    profilesWithAvatar: 0,
    profilesWithoutAvatar: 0,
    profilesWithPhotos: 0,
    profilesWithoutPhotos: 0,
    profilesWithBio: 0,
    profilesWithoutBio: 0,
    profilesWithEmail: 0,
    profilesWithoutEmail: 0,
    profilesWithTags: 0,
    profilesWithoutTags: 0,
    profilesWithSocials: 0,
    profilesWithoutSocials: 0,
    profilesWithRelationship: 0,
    profilesWithLookingFor: 0,
    profilesWithLanguages: 0,
    profilesWithEducation: 0,
    profilesWithOccupation: 0,
    profilesWithJobTitle: 0,
    profilesWithMusic: 0,
    profilesWithoutMusic: 0
  };
  
  try {
    // Pre-fetch all profile IDs in one transaction
    console.log('Step 1/4: Pre-fetching profile IDs...');
    if (progressCallback) {
      try {
        progressCallback({ phase: 'fetching_ids', index: 0, total: count });
      } catch (e) {
        console.warn('Progress callback error:', e);
      }
    }
    const profileIds = await getNextProfileIds(count);
    console.log(`✓ Fetched ${profileIds.length} profile IDs`);
    let profileIdIndex = 0;
  
  // Generate all profile data first (fast, no I/O)
  console.log('Step 2/4: Generating profile data...');
  const profileDataList = [];
  for (let i = 0; i < count; i++) {
    // Update progress for every profile during generation
    if (progressCallback) {
      try {
        progressCallback({ phase: 'generating', index: i + 1, total: count });
      } catch (e) {
        console.warn('Progress callback error:', e);
      }
    }
    if ((i + 1) % 10 === 0) {
      console.log(`  Generated ${i + 1}/${count} profiles...`);
    }
    try {
      const getRandom = () => Math.random();
      // Randomly decide whether profile should have a wallet (50% chance)
      const hasWallet = getRandom() < 0.5;
      const walletAddress = hasWallet ? generateRandomWallet() : null;
      const profileData = generateRandomProfile(i, nameOrder);
      
      // Track statistics
      if (profileData.city) stats.profilesWithCity++; else stats.profilesWithoutCity++;
      if (profileData.name) stats.profilesWithName++; else stats.profilesWithoutName++;
      if (profileData.birthDate) stats.profilesWithBirthDate++; else stats.profilesWithoutBirthDate++;
      if (hasWallet) stats.profilesWithWallet++; else stats.profilesWithoutWallet++;
      if (profileData.avatar) stats.profilesWithAvatar++; else stats.profilesWithoutAvatar++;
      if (profileData.photos && profileData.photos.length > 0) stats.profilesWithPhotos++; else stats.profilesWithoutPhotos++;
      if (profileData.bio) stats.profilesWithBio++; else stats.profilesWithoutBio++;
      if (profileData.email) stats.profilesWithEmail++; else stats.profilesWithoutEmail++;
      if (profileData.tagsList?.length > 0) stats.profilesWithTags++; else stats.profilesWithoutTags++;
      if (profileData.socialLinks?.length > 0) stats.profilesWithSocials++; else stats.profilesWithoutSocials++;
      if (profileData.relationshipStatus) stats.profilesWithRelationship++;
      if (profileData.lookingFor) stats.profilesWithLookingFor++;
      if (profileData.languages) stats.profilesWithLanguages++;
      if (profileData.education) stats.profilesWithEducation++;
      if (profileData.occupation) stats.profilesWithOccupation++;
      if (profileData.role) stats.profilesWithJobTitle++;
      if (profileData.musicList && profileData.musicList.length > 0) stats.profilesWithMusic++; else stats.profilesWithoutMusic++;
      
      // Handle city location offsets
      const city = profileData.city;
      if (city && cityUsage.has(city)) {
        const baseLocation = profileData.location;
        const offsetLat = (getRandom() * 0.04 - 0.02);
        const offsetLng = (getRandom() * 0.04 - 0.02);
        const usedLocations = cityUsage.get(city);
        let attempts = 0;
        let finalLat = baseLocation.lat + offsetLat;
        let finalLng = baseLocation.lng + offsetLng;
        const minDistance = 0.005;
        
        while (attempts < 10) {
          let tooClose = false;
          for (const used of usedLocations) {
            const dist = Math.sqrt(
              Math.pow(finalLat - used.lat, 2) + Math.pow(finalLng - used.lng, 2)
            );
            if (dist < minDistance) {
              tooClose = true;
              break;
            }
          }
          if (!tooClose) break;
          finalLat = baseLocation.lat + (getRandom() * 0.04 - 0.02);
          finalLng = baseLocation.lng + (getRandom() * 0.04 - 0.02);
          attempts++;
        }
        
        profileData.location = {
          ...baseLocation,
          lat: finalLat,
          lng: finalLng
        };
        usedLocations.push({ lat: finalLat, lng: finalLng });
      } else if (city) {
        cityUsage.set(city, [{ lat: profileData.location.lat, lng: profileData.location.lng }]);
      }
      
      profileDataList.push({
        index: i,
        walletAddress,
        hasWallet,
        profileData,
        userId: profileIds[profileIdIndex++]
      });
    } catch (error) {
      console.error(`Error generating profile ${i + 1}:`, error);
      errors.push({ index: i + 1, error: error.message });
    }
  }
  
  // Process profiles in parallel but save each one immediately as it completes
  // This gives us speed of parallel processing + safety of immediate saves
  console.log('Step 3/4: Processing profiles in parallel (each saved immediately)...');
  let CONCURRENCY = 3; // Start with 3 to avoid rate limiting, will adapt if needed
  let completedCount = 0;
  const completedProfiles = new Array(count).fill(null); // Track completion order
  let consecutiveErrors = 0;
  const BATCH_DELAY = 200; // Small delay between batches to avoid rate limiting
  
  // Retry helper with exponential backoff
  const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        // Check if it's a permission error - don't retry those
        if (error.code === 'permission-denied' || 
            error.message?.includes('Missing or insufficient permissions') ||
            error.message?.includes('permission')) {
          throw error;
        }
        
        // Check if it's a network blocking error - log but continue retrying
        if (error.message?.includes('ERR_BLOCKED_BY_CLIENT') || 
            error.message?.includes('Failed to load resource')) {
          console.warn(`⚠️ Network blocking detected (attempt ${attempt + 1}/${maxRetries}). This may be caused by browser extensions.`);
        }
        
        if (attempt === maxRetries - 1) throw error;
        const delay = baseDelay * Math.pow(2, attempt);
        console.warn(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };
  
  // Helper function to replace avatar/photos data with placeholders before encryption
  const replaceAvatarDataWithPlaceholders = (data) => {
    if (!data || typeof data !== 'object') return data;
    
    // Create a deep copy to avoid mutating the original
    const sanitized = Array.isArray(data) ? [...data] : { ...data };
    
    for (const key in sanitized) {
      if (key === 'avatar' && sanitized[key] != null && typeof sanitized[key] === 'string') {
        // Replace avatar with placeholder
        sanitized[key] = '[avatar1_picture_code]';
      } else if (key === 'photos' && Array.isArray(sanitized[key])) {
        // Replace each photo in the array with numbered placeholders
        sanitized[key] = sanitized[key].map((photo, index) => {
          if (photo != null && typeof photo === 'string') {
            return `[avatar${index + 1}_picture_code]`;
          }
          return photo;
        });
      } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        // Recursively process nested objects
        sanitized[key] = replaceAvatarDataWithPlaceholders(sanitized[key]);
      }
    }
    
    return sanitized;
  };

  // Helper to process a single profile completely (generate -> encrypt -> upload -> save)
  const processAndSaveProfile = async ({ index, walletAddress, hasWallet, profileData, userId }) => {
    try {
      // Step 1: Encrypt and upload to IPFS with retry (only if profile has wallet)
      let ipfsHash = null;
      if (hasWallet && walletAddress) {
        if (progressCallback) {
          try {
            progressCallback({ phase: 'uploading_ipfs', index: index + 1, total: count });
          } catch (e) {
            console.warn('Progress callback error:', e);
          }
        }
        
        // Replace avatar/photos with placeholders before encryption
        const profileDataWithPlaceholders = replaceAvatarDataWithPlaceholders(profileData);
        
        const encryptionKey = generateEncryptionKeyFromAddress(walletAddress);
        const encryptedData = encryptProfile(profileDataWithPlaceholders, encryptionKey);
        
        // Upload to IPFS with retry and backoff
        ipfsHash = await retryWithBackoff(async () => {
          return await uploadToPinata(encryptedData);
        }, 3, 2000); // 3 retries, starting with 2s delay
        
        // Small delay after IPFS upload to avoid overwhelming Pinata
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Step 2: Save to Firestore immediately (no waiting for batch)
      if (progressCallback) {
        try {
          progressCallback({ phase: 'saving', index: index + 1, total: count });
        } catch (e) {
          console.warn('Progress callback error:', e);
        }
      }
      
      const normalizedAddress = walletAddress ? walletAddress.toLowerCase() : null;
      const profileRef = doc(db, 'profile_test', profileData.username);
      
      const cityValue = profileData.city != null ? profileData.city : null;
      const locationValue = (profileData.location && profileData.location.lat != null && profileData.location.lng != null)
        ? {
            lat: profileData.location.lat,
            lng: profileData.location.lng,
            city: profileData.location.city || profileData.city || null
          }
        : null;
      const nameValue = profileData.name != null ? profileData.name : null;
      const bioValue = profileData.bio != null ? profileData.bio : null;
      const emailValue = profileData.email != null ? profileData.email : null;
      const relationshipStatusValue = profileData.relationshipStatus != null ? profileData.relationshipStatus : null;
      const lookingForValue = profileData.lookingFor != null ? profileData.lookingFor : null;
      const languagesValue = profileData.languages != null ? profileData.languages : null;
      const educationValue = profileData.education != null ? profileData.education : null;
      const occupationValue = profileData.occupation != null ? profileData.occupation : null;
      const roleValue = profileData.role != null ? profileData.role : null;
      const musicListValue = Array.isArray(profileData.musicList) ? profileData.musicList : [];

      const dataToSave = {
        username: profileData.username,
        user_id: userId,
        // Only include walletAddress if profile has a wallet (randomly assigned)
        walletAddress: normalizedAddress,
        avatar: profileData.avatar || null,
        photos: Array.isArray(profileData.photos) ? profileData.photos : [],
        name: nameValue,
        bio: bioValue,
        email: emailValue,
        city: cityValue,
        location: locationValue,
        tagsList: Array.isArray(profileData.tagsList) ? profileData.tagsList : [],
        socialLinks: Array.isArray(profileData.socialLinks) ? profileData.socialLinks : [],
        groups: Array.isArray(profileData.groups) ? profileData.groups : [],
        musicList: musicListValue,
        birthDate: profileData.birthDate || null,
        age: profileData.age != null ? profileData.age : null,
        relationshipStatus: relationshipStatusValue,
        lookingFor: lookingForValue,
        languages: languagesValue,
        education: educationValue,
        occupation: occupationValue,
        role: roleValue,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        _isTestRecord: true
      };

      // Save immediately with retry (Firestore can also rate limit)
      // For test profiles: Always try to delete first (ignore errors), then create fresh
      // This ensures we always do a CREATE operation, which is simpler for security rules
      console.log(`[${profileData.username}] Starting Firestore save operation...`);
      try {
        await retryWithBackoff(async () => {
          // Always try to delete first (ignore errors - document might not exist)
          // This ensures we start with a clean slate and always do CREATE, not UPDATE
          try {
            console.log(`[${profileData.username}] Attempting to delete existing document (if any)...`);
            await deleteDoc(profileRef);
            // Wait for delete to fully propagate (Firestore needs time)
            console.log(`[${profileData.username}] Delete successful, waiting for propagation...`);
            await new Promise(resolve => setTimeout(resolve, 1500));
          } catch (deleteError) {
            // Ignore delete errors - document might not exist, which is fine
            // Only log if it's not a "not-found" error
            if (deleteError.code === 'not-found') {
              console.log(`[${profileData.username}] Document does not exist (expected for new profiles)`);
            } else {
              console.warn(`[${profileData.username}] Delete attempt result: ${deleteError.code || deleteError.message} (continuing anyway)`);
            }
            // Still wait a bit even if delete failed, in case it was a timing issue
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          // Now create the document fresh (always a CREATE operation, never UPDATE)
          // This ensures the create rule is used, not the update rule
          const walletDisplay = normalizedAddress ? normalizedAddress.substring(0, 10) + '...' : 'null (no wallet)';
          console.log(`[${profileData.username}] Creating new document with walletAddress: ${walletDisplay}`);
          console.log(`[${profileData.username}] Data validation: username=${dataToSave.username}, walletAddress=${dataToSave.walletAddress ? 'present' : 'null'}, _isTestRecord=${dataToSave._isTestRecord}`);
          await setDoc(profileRef, dataToSave);
          console.log(`[${profileData.username}] ✓ Successfully created document`);
        }, 3, 1000); // 3 retries, starting with 1s delay
      } catch (saveError) {
        // Log detailed error information
        console.error(`[${profileData.username}] Firestore save failed:`, {
          code: saveError.code,
          message: saveError.message,
          stack: saveError.stack?.split('\n')[0]
        });
        throw saveError; // Re-throw to be caught by outer handler
      }
      
      // Step 3: Create gamification data with random badges (only if user has wallet)
      if (hasWallet && normalizedAddress) {
        try {
          // Generate random badges (1-6 badges per user)
          const allBadgeIds = Object.values(BADGES).map(badge => badge.id);
          const numBadges = Math.floor(Math.random() * 6) + 1; // 1-6 badges
          const selectedBadges = [];
          const availableBadges = [...allBadgeIds];
          
          for (let i = 0; i < numBadges && availableBadges.length > 0; i++) {
            const randomIndex = Math.floor(Math.random() * availableBadges.length);
            selectedBadges.push(availableBadges.splice(randomIndex, 1)[0]);
          }
          
          // Calculate points based on badges
          const totalPoints = selectedBadges.reduce((sum, badgeId) => {
            const badgeKey = Object.keys(BADGES).find(key => BADGES[key].id === badgeId);
            const badge = badgeKey ? BADGES[badgeKey] : null;
            return sum + (badge?.points || 10);
          }, 0);
          
          // Create gamification document
          const gamificationRef = doc(db, 'gamification', normalizedAddress);
          const gamificationData = {
            walletAddress: normalizedAddress,
            badges: selectedBadges,
            points: totalPoints,
            currentStreak: Math.floor(Math.random() * 30) + 1, // Random streak 1-30
            longestStreak: Math.floor(Math.random() * 50) + 1, // Random longest streak 1-50
            totalVisits: Math.floor(Math.random() * 100) + 1, // Random visits 1-100
            profileCompleteness: Math.floor(Math.random() * 100), // Random completeness 0-100
            messagesSent: Math.floor(Math.random() * 20), // Random messages 0-20
            profilesViewed: Math.floor(Math.random() * 100), // Random profiles viewed 0-100
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          };
          
          await setDoc(gamificationRef, gamificationData);
          console.log(`[${profileData.username}] ✓ Created gamification data with ${selectedBadges.length} badges`);
        } catch (gamificationError) {
          // Don't fail the whole operation if gamification creation fails
          console.warn(`[${profileData.username}] Warning: Could not create gamification data:`, gamificationError.message);
        }
      }
      
      // Small delay to avoid overwhelming the services
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Step 4: Call progress callback with profile so it appears on map immediately
      const savedProfile = {
        walletAddress: walletAddress || null,
        username: profileData.username,
        name: profileData.name,
        ...profileData,
        _isTestRecord: true
      };
      
      completedCount++;
      completedProfiles[index] = savedProfile; // Store for later collection
      
      if (progressCallback) {
        try {
          progressCallback({
            phase: 'complete',
            index: index + 1,
            total: count,
            profile: savedProfile
          });
        } catch (e) {
          console.warn('Progress callback error:', e);
        }
      }
      
      // Log progress every 10 profiles
      if (completedCount % 10 === 0 || completedCount === count) {
        console.log(`  ✓ Saved ${completedCount}/${count} profiles (${(completedCount / count * 100).toFixed(1)}%)`);
      }
      
      consecutiveErrors = 0; // Reset on success
      return { success: true, index, profile: savedProfile };
    } catch (error) {
      // Provide more detailed error information
      let errorMessage = error.message || 'Unknown error';
      
      if (error.code === 'permission-denied' || 
          error.message?.includes('Missing or insufficient permissions')) {
        errorMessage = `Permission denied: ${error.message}. `;
        const walletDisplay = walletAddress ? walletAddress.substring(0, 10) + '...' : 'null (no wallet)';
        errorMessage += `Profile: ${profileData.username}, Wallet: ${walletDisplay} `;
        errorMessage += `Ensure Firestore security rules allow writes to profile_test collection. `;
        errorMessage += `The document may already exist with a different walletAddress.`;
      }
      
      console.error(`Error processing profile ${index + 1} (${profileData.username}):`, errorMessage);
      if (error.code) {
        console.error(`  Error code: ${error.code}`);
      }
      if (error.stack) {
        console.error(`  Stack: ${error.stack.split('\n')[0]}`);
      }
      completedCount++;
      consecutiveErrors++;
      
      // If we're getting rate limited, reduce concurrency
      if (consecutiveErrors >= 3 && CONCURRENCY > 1) {
        const oldConcurrency = CONCURRENCY;
        CONCURRENCY = Math.max(1, CONCURRENCY - 1);
        console.warn(`⚠️ Rate limiting detected (${consecutiveErrors} errors), reducing concurrency from ${oldConcurrency} to ${CONCURRENCY}`);
        consecutiveErrors = 0; // Reset counter
        // Add extra delay when rate limited
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      return { success: false, index, error: error.message };
    }
  };
  
  // Process profiles in parallel batches with concurrency limit and adaptive rate limiting
  for (let i = 0; i < profileDataList.length; i += CONCURRENCY) {
    const batch = profileDataList.slice(i, i + CONCURRENCY);
    const batchStartTime = Date.now();
    
    // Process batch in parallel - each profile saves immediately when done
    const results = await Promise.all(batch.map(processAndSaveProfile));
    
    // Collect errors from failed profiles
    for (const result of results) {
      if (!result.success) {
        errors.push({ index: result.index + 1, error: result.error });
      }
    }
    
    // Add delay between batches to avoid rate limiting (except for last batch)
    if (i + CONCURRENCY < profileDataList.length) {
      const batchTime = Date.now() - batchStartTime;
      const avgTimePerProfile = batchTime / batch.length;
      
      // If batch completed quickly, add delay to avoid rate limiting
      // If batch was slow (already rate limited), add longer delay
      if (avgTimePerProfile < 2000) {
        // Fast batch - small delay to prevent rate limiting
        await new Promise(resolve => setTimeout(resolve, BATCH_DELAY));
      } else if (avgTimePerProfile > 5000) {
        // Slow batch - likely rate limited, add longer delay
        console.warn(`Slow batch detected (${avgTimePerProfile.toFixed(0)}ms/profile), adding longer delay...`);
        await new Promise(resolve => setTimeout(resolve, BATCH_DELAY * 3));
      }
    }
  }
  
  // Collect all successfully saved profiles from completedProfiles array
  for (const savedProfile of completedProfiles) {
    if (savedProfile) {
      profiles.push(savedProfile);
    }
  }
  
  console.log(`✓ All profiles processed. Successfully saved: ${profiles.length}/${count}`);
  
  const elapsedTime = ((Date.now() - startTime) / 1000).toFixed(2);
  console.log(`\n=== Population Complete (${elapsedTime}s) ===`);
  console.log(`Successfully created: ${profiles.length} profiles`);
  console.log(`  Profiles with city: ${stats.profilesWithCity}`);
  console.log(`  Profiles without city: ${stats.profilesWithoutCity}`);
  console.log(`  Profiles with name: ${stats.profilesWithName}`);
  console.log(`  Profiles without name: ${stats.profilesWithoutName}`);
  console.log(`  Profiles with birth date: ${stats.profilesWithBirthDate}`);
  console.log(`  Profiles without birth date: ${stats.profilesWithoutBirthDate}`);
  console.log(`  Profiles with wallet: ${stats.profilesWithWallet}`);
  console.log(`  Profiles without wallet: ${stats.profilesWithoutWallet}`);
  console.log(`  Profiles with avatar: ${stats.profilesWithAvatar}`);
  console.log(`  Profiles without avatar: ${stats.profilesWithoutAvatar}`);
  console.log(`  Profiles with photos: ${stats.profilesWithPhotos}`);
  console.log(`  Profiles without photos: ${stats.profilesWithoutPhotos}`);
  console.log(`  Profiles with bio: ${stats.profilesWithBio}`);
  console.log(`  Profiles without bio: ${stats.profilesWithoutBio}`);
  console.log(`  Profiles with email: ${stats.profilesWithEmail}`);
  console.log(`  Profiles without email: ${stats.profilesWithoutEmail}`);
  console.log(`  Profiles with tags: ${stats.profilesWithTags}`);
  console.log(`  Profiles without tags: ${stats.profilesWithoutTags}`);
  console.log(`  Profiles with socials: ${stats.profilesWithSocials}`);
  console.log(`  Profiles without socials: ${stats.profilesWithoutSocials}`);
  console.log(`  Profiles with relationship: ${stats.profilesWithRelationship}`);
  console.log(`  Profiles with looking for: ${stats.profilesWithLookingFor}`);
  console.log(`  Profiles with languages: ${stats.profilesWithLanguages}`);
  console.log(`  Profiles with education: ${stats.profilesWithEducation}`);
  console.log(`  Profiles with occupation: ${stats.profilesWithOccupation}`);
  console.log(`  Profiles with job title: ${stats.profilesWithJobTitle}`);
  console.log(`  Profiles with music: ${stats.profilesWithMusic}`);
  console.log(`  Profiles without music: ${stats.profilesWithoutMusic}`);
  console.log(`Errors: ${errors.length}`);
  
    if (errors.length > 0) {
      console.log('\nErrors:');
      errors.forEach(err => {
        console.log(`  Profile ${err.index}: ${err.error}`);
      });
    }
    
    return {
      success: profiles.length,
      errors: errors.length,
      profiles,
      elapsedTime: parseFloat(elapsedTime)
    };
  } catch (error) {
    console.error('Fatal error in populateDatabase:', error);
    if (progressCallback) {
      try {
        progressCallback({ 
          phase: 'error', 
          index: 0, 
          total: count,
          error: error.message 
        });
      } catch (e) {
        // Ignore callback errors
      }
    }
    return {
      success: profiles.length,
      errors: errors.length + 1,
      profiles,
      error: error.message,
      elapsedTime: ((Date.now() - startTime) / 1000).toFixed(2)
    };
  }
};

// Make it available globally for browser console access
if (typeof window !== 'undefined') {
  window.populateDatabase = populateDatabase;
  console.log('Database population function available. Call: populateDatabase()');
}

